// ============================================================================
// USE CONFIG SETTINGS HOOK
// ============================================================================
// Backing store for the Settings admin screen (ConfigSettingsScreen).
// Distinct from useConfig(groupKey) — that's the consumer-facing hook for
// domain screens; THIS hook is admin-only, exposes CRUD, and tracks
// per-scope editable values.
//
// Data flow:
//   1. Load groups from /config-groups                     → sidebar tabs.
//   2. On group select: load definitions in that group     → field list.
//   3. On group select: load effective values via /config/effective
//      → tells us the CURRENT resolved value + which scope produced it.
//   4. Load scope rows for the picker via /config-scopes   → dropdown options.
//   5. User edits values; save writes to /config-values (create if none at
//      current scope; update if exists; delete if reset-to-inherited).
//
// Scope picker: 6-tier hierarchy per plan D-9 —
//   SYSTEM → ENTERPRISE → ENTITY → BUSINESS_UNIT → FINANCIAL_YEAR → VOUCHER_TYPE
// User selects ONE (scope_type, scope_target) pair; screen shows the effective
// value at THAT scope + all its ancestors, with ScopeChip indicating source.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { configService } from 'api/services/configService';
import type {
  ConfigDefinitionDto,
  ConfigGroupDto,
  ConfigScopeDto,
  ConfigScopeType,
  ConfigValueDto,
  EffectiveConfigEntry,
} from 'api/types/config.types';
import { notificationService } from 'services/notificationService';

/** One pending edit — undefined value = reset-to-inherited (delete override). */
export interface PendingEdit {
  configKey: string;
  /** Scalar new value (undefined = clear override). */
  value?: string;
  /** List items (for is_multi_value definitions). */
  items?: string[];
  /** Existing tbl_config_value.id at this scope, if any — for update vs create. */
  existingValueId?: string;
  rowVersion?: number;
}

export interface UseConfigSettingsResult {
  // Data
  groups: ConfigGroupDto[];
  groupsLoading: boolean;
  definitions: ConfigDefinitionDto[];
  effectiveEntries: EffectiveConfigEntry[];
  loading: boolean;
  scopes: ConfigScopeDto[];
  scopesLoading: boolean;

  // Selection state
  activeGroupKey: string | null;
  activeScopeType: ConfigScopeType;
  activeScopeId: string | null;

  // Editing state
  editedValues: Record<string, PendingEdit>;
  hasChanges: boolean;
  saving: boolean;

  // Selection actions
  selectGroup: (groupKey: string) => void;
  selectScopeType: (scopeType: ConfigScopeType) => void;
  selectScopeTarget: (scopeId: string | null) => void;

  // Editing actions
  updateValue: (configKey: string, value?: string, items?: string[]) => void;
  resetValue: (configKey: string) => void;
  discardChanges: () => void;
  saveChanges: () => Promise<void>;
}

/**
 * Which scope types are SINGLETONS per tenant DB and therefore need no
 * target-picker in the UI. Both auto-load their single scope row and
 * auto-set activeScopeId so fields render immediately.
 *   SYSTEM     — framework default; one row per DB (plan D-9 root).
 *   ENTERPRISE — the tenant itself; one row per DB (ADR-0002 "one enterprise
 *                per tenant DB" + plan D-8 "ENTERPRISE:tenant is 1:1"). The
 *                tenant name is already visible in the app header, so a
 *                dropdown with one option is pointless friction.
 * Exported so the panel can hide the target picker for these types.
 */
export function isSingletonScopeType(scopeType: ConfigScopeType): boolean {
  return scopeType === 'SYSTEM' || scopeType === 'ENTERPRISE';
}

export function useConfigSettings(): UseConfigSettingsResult {
  // ─── data ────────────────────────────────────────────────────────────
  const [groups, setGroups] = useState<ConfigGroupDto[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [definitions, setDefinitions] = useState<ConfigDefinitionDto[]>([]);
  const [effectiveEntries, setEffectiveEntries] = useState<EffectiveConfigEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [scopes, setScopes] = useState<ConfigScopeDto[]>([]);
  const [scopesLoading, setScopesLoading] = useState(false);

  // ─── selection state ─────────────────────────────────────────────────
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [activeScopeType, setActiveScopeType] = useState<ConfigScopeType>('SYSTEM');
  const [activeScopeId, setActiveScopeId] = useState<string | null>(null);

  // ─── editing state ───────────────────────────────────────────────────
  const [editedValues, setEditedValues] = useState<Record<string, PendingEdit>>({});
  const [saving, setSaving] = useState(false);

  const hasChanges = useMemo(() => Object.keys(editedValues).length > 0, [editedValues]);

  // ─── load groups on mount ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await configService.getGroups();
        if (!cancelled) {
          setGroups(list);
          // Auto-select first group so panel isn't blank on first load.
          if (list.length > 0 && !activeGroupKey) {
            setActiveGroupKey(list[0].groupKey);
          }
        }
      } catch (err) {
        if (!cancelled) notificationService.toast.error('Failed to load config groups.');
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── load scopes when scope-type changes ──────────────────────────────
  // For SINGLETON scope types (SYSTEM, ENTERPRISE) we still load — the save
  // path needs the row's id to know where to write — but we ALSO auto-set
  // activeScopeId to the single row so the UI skips the target picker and
  // renders fields immediately.
  useEffect(() => {
    let cancelled = false;
    setScopesLoading(true);
    (async () => {
      try {
        const list = await configService.getScopes(activeScopeType);
        if (cancelled) return;
        setScopes(list);
        // Singleton auto-select — first scope row wins (there's guaranteed to
        // be only one per tenant DB per plan D-8 / ADR-0002).
        if (isSingletonScopeType(activeScopeType) && list.length > 0) {
          setActiveScopeId(list[0].id);
        }
      } catch (err) {
        if (!cancelled) notificationService.toast.error(`Failed to load ${activeScopeType} scopes.`);
      } finally {
        if (!cancelled) setScopesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeScopeType]);

  // ─── load definitions + effective values on group change ─────────────
  useEffect(() => {
    if (!activeGroupKey) return;
    const group = groups.find((g) => g.groupKey === activeGroupKey);
    if (!group) return;

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [defs, effective] = await Promise.all([
          configService.getDefinitions(group.id),
          configService.effective(activeGroupKey),
        ]);
        if (!cancelled) {
          setDefinitions(defs);
          setEffectiveEntries(effective.entries);
        }
      } catch (err) {
        if (!cancelled) notificationService.toast.error('Failed to load config for this group.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeGroupKey, groups]);

  // ─── selection actions ────────────────────────────────────────────────
  const selectGroup = useCallback((groupKey: string) => {
    setActiveGroupKey(groupKey);
    setEditedValues({});
  }, []);

  const selectScopeType = useCallback((scopeType: ConfigScopeType) => {
    setActiveScopeType(scopeType);
    setActiveScopeId(null);
    setEditedValues({});
  }, []);

  const selectScopeTarget = useCallback((scopeId: string | null) => {
    setActiveScopeId(scopeId);
    setEditedValues({});
  }, []);

  // ─── editing actions ──────────────────────────────────────────────────
  const updateValue = useCallback(
    (configKey: string, value?: string, items?: string[]) => {
      setEditedValues((prev) => ({
        ...prev,
        [configKey]: { configKey, value, items },
      }));
    },
    [],
  );

  const resetValue = useCallback((configKey: string) => {
    // Mark for delete (undefined value + undefined items = clear override).
    setEditedValues((prev) => ({
      ...prev,
      [configKey]: { configKey, value: undefined, items: undefined },
    }));
  }, []);

  const discardChanges = useCallback(() => {
    setEditedValues({});
  }, []);

  const saveChanges = useCallback(async () => {
    if (!activeGroupKey || !hasChanges) return;

    // activeScopeId is guaranteed to be set for both singleton (SYSTEM /
    // ENTERPRISE — auto-selected in the scope-load effect) and multi-target
    // (ENTITY / BUSINESS_UNIT / FINANCIAL_YEAR / VOUCHER_TYPE — set when the
    // user picked a target). If it's null here, save is not valid.
    const targetScopeId: string | null = activeScopeId;

    if (!targetScopeId) {
      notificationService.toast.error('Select a scope target before saving.');
      return;
    }

    setSaving(true);
    try {
      // For each edit, find the definition + existing value row at target scope.
      const existingValues = await configService.getValues(undefined, targetScopeId);

      const writes: Promise<unknown>[] = [];
      for (const edit of Object.values(editedValues)) {
        const def = definitions.find((d) => d.configKey === edit.configKey);
        if (!def) continue;

        const existing = existingValues.find((v) => v.configDefinitionId === def.id);

        // Reset (undefined value AND undefined items) → delete existing row (if any).
        if (edit.value === undefined && !edit.items) {
          if (existing) writes.push(configService.deleteValue(existing.id));
          continue;
        }

        // Multi-value: not handled through tbl_config_value; needs list-item CRUD (F3.5+).
        if (def.isMultiValue) {
          // For now emit a toast — multi-value edit from Settings screen not yet wired.
          notificationService.toast.warning(
            `Multi-value setting '${def.configKey}' can only be edited via the List admin UI.`,
          );
          continue;
        }

        if (existing) {
          writes.push(
            configService.updateValue(existing.id, {
              configValue: edit.value ?? '',
              rowVersion: (existing as ConfigValueDto & { rowVersion?: number }).rowVersion ?? 1,
            }),
          );
        } else {
          writes.push(
            configService.createValue({
              configDefinitionId: def.id,
              configScopeId: targetScopeId!,
              configValue: edit.value ?? '',
            }),
          );
        }
      }

      await Promise.all(writes);

      // Refetch effective values so ScopeChips reflect the new state.
      const fresh = await configService.effective(activeGroupKey);
      setEffectiveEntries(fresh.entries);
      setEditedValues({});
      notificationService.toast.success('Configuration saved.');
    } catch (err) {
      notificationService.toast.error('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  }, [activeGroupKey, activeScopeId, activeScopeType, definitions, editedValues, hasChanges, scopes]);

  return {
    groups,
    groupsLoading,
    definitions,
    effectiveEntries,
    loading,
    scopes,
    scopesLoading,

    activeGroupKey,
    activeScopeType,
    activeScopeId,

    editedValues,
    hasChanges,
    saving,

    selectGroup,
    selectScopeType,
    selectScopeTarget,

    updateValue,
    resetValue,
    discardChanges,
    saveChanges,
  };
}
